import { isSupabaseConfigured, supabaseProjectHost, supabaseUrl } from './supabase'

const NETWORK_ERROR_PATTERNS = [
  /failed to fetch/i,
  /fetch failed/i,
  /load failed/i,
  /networkerror/i,
  /network request failed/i
]

type AuthOperation =
  | 'signin'
  | 'signup'
  | 'signout'
  | 'doctor-signin'
  | 'doctor-create'
  | 'session'

function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message || error.name
  }

  if (typeof error === 'string') {
    return error
  }

  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message
  }

  return ''
}

function getOperationLabel(operation: AuthOperation) {
  switch (operation) {
    case 'signup':
      return 'creer le compte'
    case 'signout':
      return 'fermer la session'
    case 'doctor-signin':
      return "ouvrir l'acces medecin"
    case 'doctor-create':
      return "generer l'acces medecin"
    case 'session':
      return 'restaurer la session'
    case 'signin':
    default:
      return 'se connecter'
  }
}

function isNetworkError(message: string) {
  return NETWORK_ERROR_PATTERNS.some((pattern) => pattern.test(message))
}

export function formatSupabaseAuthError(
  error: unknown,
  options?: {
    operation?: AuthOperation
    host?: string
  }
) {
  const rawMessage = extractErrorMessage(error)
  const operation = options?.operation || 'signin'
  const host = options?.host || supabaseProjectHost

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return 'Connexion internet absente. Verifiez votre reseau puis reessayez.'
  }

  if (isNetworkError(rawMessage)) {
    return `Impossible de ${getOperationLabel(operation)} car le serveur de connexion (${host}) est injoignable. Si le probleme persiste, la configuration Supabase de la production doit etre verifiee.`
  }

  if (/invalid login credentials/i.test(rawMessage)) {
    return 'Identifiants invalides. Verifiez l email et le mot de passe.'
  }

  if (!rawMessage.trim()) {
    return `Impossible de ${getOperationLabel(operation)} pour le moment.`
  }

  return rawMessage
}

export async function checkSupabaseAuthReachability(timeoutMs = 4000): Promise<{
  reachable: boolean
  message: string | null
  host: string
}> {
  if (!isSupabaseConfigured || !supabaseUrl) {
    return {
      reachable: false,
      message: "La configuration Supabase n'est pas renseignee.",
      host: supabaseProjectHost
    }
  }

  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs)

  try {
    const endpoint = new URL('/auth/v1/settings', supabaseUrl).toString()
    const response = await fetch(endpoint, {
      method: 'GET',
      signal: controller.signal
    })

    if (response.ok) {
      return {
        reachable: true,
        message: null,
        host: supabaseProjectHost
      }
    }

    return {
      reachable: false,
      message: `Le serveur de connexion (${supabaseProjectHost}) repond avec le statut ${response.status}.`,
      host: supabaseProjectHost
    }
  } catch (error) {
    return {
      reachable: false,
      message: formatSupabaseAuthError(error, { operation: 'session' }),
      host: supabaseProjectHost
    }
  } finally {
    window.clearTimeout(timeout)
  }
}
